// A world provider for any web page (runtime/README.md, "World provider"). Companions stand on
// headings (just above their letters' ink: ink.js), sit at the end of lines of text, on search
// fields and on the tops of cards, images and buttons, and never where their body would cover
// text or controls.
//
//   const world = createPageWorld({ root: document, exclude: [overlayHost] });
//   world.snapshot();          // { viewport: { width, height, dpr }, surfaces, occluders, floor }
//   world.subscribe(changed);  // changed() when the page changed; returns unsubscribe
//   world.clearFloor();        // [[x1, x2], ...]: where on the floor no content passes under (also floor.rest)
//   world.destroy();           // removes every observer and listener it added, and its probe element
//
// The floor is the bottom of what is visible: above a phone's keyboard, home indicator and a fixed
// bar along the bottom, inside the side safe areas. It lies over the page, so its weight is low:
// companions would rather stand on the page's own spots. They may walk across it anywhere, but stay
// (stand around, sit, nap, a bed or a party) only where no content passes under it: floor.rest.
//
// Coordinates are client (viewport) CSS pixels: the overlay canvas is position: fixed at 0,0 and as
// big as the layout viewport. Discovery (page-scan.js) runs when the page changes; every frame only
// re-reads the rectangles of what it found on or near the screen.
//
// Scrolling: a companion rides its surface. When the surface reaches the top of the screen (or a
// sticky header) with no room left for the body, or sinks below the floor, it leaves the snapshot:
// the companion falls onto what is below it, or simply stands on the floor. With `riders`, the host
// lists occupied surface ids: those keep their element's geometry off screen (`riding: true`) until
// they return, and under something on top of them (`riding: true, covered: true`: a dialog, a banner)
// until it goes. The runtime's `ride` option waits there instead of falling or starting another trip.
import { env } from '../engine/core.js';
import { scanPage, seeThrough } from './page-scan.js';
import { createInk } from './ink.js';

const MIN_BODY = 7;           // room above a spot for the smallest companion, in its pixels (Clawd)
const TALL_BODY = 16;         // ...and for a tall one (most are 9 to 16 rows), against the screen's top and fixed bars
const ROOM_CAP = 40;          // room is measured up to this, in companion pixels
const LEGS = 3;               // legs dangling below a line end
const SEAT = 48;              // a line end's seat, in companion pixels: the widest companion (the Ender Dragon) needs 44
// How far below its feet a companion standing there draws, in its pixels: the turned ends of Ferris's
// and the spider's splayed legs, Ares's spear (measured from the engine's drawing of every design; a
// step while walking can dip a little lower for a moment).
const FEET = 0.5;
const BAND = 2;               // discovery reaches this many screen heights above and below
const GAP = 450;              // ms between rediscoveries while the page keeps changing
const SAME_FRAME = 4;         // ms: snapshots this close together share their geometry
const STAY_COVERED = 800;     // ms a ridden spot stays covered after something was last on top of it (a dialog fading in or out)
const HIT_EVERY = 100, HIT_BATCH = 6;
const FLOOR_WEIGHT = 0.3;     // the floor's weight (the engine's default is 1.2): it lies over the page's content
const CONTENT = new Set(['control', 'search', 'media']);   // owners that clearFloor() keeps free (with text)
const EMPTY = Object.freeze({ viewport: { width: 0, height: 0, dpr: 1 }, surfaces: [], occluders: [] });

export function createPageWorld({ root = document, exclude = [], maxSurfaces = 200, budget = 5000, riders = null } = {}) {
  const doc = root.nodeType === 9 ? root : root.ownerDocument, win = doc.defaultView;
  const excluded = new Set(exclude.filter(Boolean));
  const ids = new WeakMap();
  let nextId = 1;
  const idOf = el => { let n = ids.get(el); if (!n) { n = nextId++; ids.set(el, n); } return 'w' + n; };
  // Geometry freshness and throttling only; companions' animation time stays on the runtime clock.
  const now = () => win.performance.now();

  let scan = null, scans = 0, scannedAt = -Infinity, scannedScrollY = 0, dirty = true, destroyed = false, timer = null;
  let cache = null, cacheAt = -Infinity, stale = true, safe = null, probe = null;
  let scrollEpoch = 0, lastScroll = -Infinity, lastHits = -Infinity;
  const subscribers = new Set(), watchedRoots = new Set(), byElement = new Map();
  // Measured lines outlive a rediscovery while the page's content and fonts stay the same.
  const lineCache = new WeakMap();
  const ink = createInk(doc);
  const tracked = new Map();  // occupied spots keep their element even when discovery moves on
  const covered = new Map();  // this frame's spots left out only because something is on top of them
  let content = 0;
  const events = new AbortController(), on = (target, type, fn, options = {}) => target?.addEventListener(type, fn, { ...options, signal: events.signal });

  const inExcluded = node => {
    for (const host of excluded) if (host === node || host.contains(node)) return true;
    return false;
  };
  const ours = node => node === probe || excluded.has(node) || (node.isConnected && inExcluded(node));
  const relevant = record => {
    if (ours(record.target)) return false;
    if (record.type !== 'childList') return true;
    const nodes = [...record.addedNodes, ...record.removedNodes];
    return !nodes.length || !nodes.every(ours);
  };

  const notify = () => { for (const fn of [...subscribers]) { try { fn(); } catch (e) { console.error(e); } } };
  function invalidate() {
    if (destroyed) return;
    dirty = true; stale = true; schedule();
  }
  function schedule() {
    if (destroyed || timer || !subscribers.size || !dirty) return;
    timer = win.setTimeout(() => { timer = null; if (!dirty || destroyed) return; rescan(); notify(); },
      Math.max(30, scannedAt + GAP - now()));
  }

  // ---------- watching the page ----------
  const watch = { subtree: true, childList: true, attributes: true, characterData: true };
  const mutations = new win.MutationObserver(records => { if (records.some(relevant)) { content++; invalidate(); } });
  const resizes = new win.ResizeObserver(() => invalidate());
  const nearby = new win.IntersectionObserver(entries => {
    for (const e of entries) for (const item of byElement.get(e.target) || []) item.near = e.isIntersecting;
  }, { rootMargin: '60% 20%' });
  mutations.observe(root, watch);
  resizes.observe(doc.documentElement);
  if (doc.body) resizes.observe(doc.body);
  function onScroll(e) {
    stale = true; scrollEpoch++; lastScroll = now();
    const nested = e.target !== doc && e.target !== doc.documentElement && e.target !== doc.scrollingElement;
    if (nested ? !ours(e.target) : Math.abs(win.scrollY - scannedScrollY) > (doc.documentElement.clientHeight || 600)) invalidate();
  }
  function onViewport() { stale = true; safe = null; invalidate(); notify(); }
  on(doc, 'scroll', onScroll, { capture: true, passive: true });
  on(doc, 'load', e => { if (!ours(e.target)) invalidate(); }, { capture: true });   // images getting their size
  on(win, 'resize', onViewport);
  on(win.visualViewport, 'resize', onViewport);
  on(win.visualViewport, 'scroll', () => { stale = true; });
  on(doc.fonts, 'loadingdone', () => { content++; ink.clear(); invalidate(); });

  // ---------- discovery ----------
  function rescan() {
    const view = readView();
    const t0 = now();
    scan = scanPage({ root, doc, win, exclude: excluded, idOf, budget, view,
      band: { top: -BAND * view.height, bottom: (1 + BAND) * view.height } });
    scan.ms = now() - t0; scans++;
    scannedAt = now(); scannedScrollY = win.scrollY; dirty = false; stale = true;
    for (const sr of scan.shadowRoots) {
      if (watchedRoots.has(sr)) continue;
      watchedRoots.add(sr); mutations.observe(sr, watch); on(sr, 'scroll', onScroll, { capture: true, passive: true });
    }
    nearby.disconnect(); byElement.clear();
    const margin = view.height * 0.6;
    for (const item of scan.items) {
      const r = item.rect;
      item.near = r.bottom > -margin && r.top < view.height + margin;
      if (!byElement.has(item.el)) { byElement.set(item.el, []); nearby.observe(item.el); }
      byElement.get(item.el).push(item);
    }
  }

  // ---------- the viewport, the safe area and the floor ----------
  function readView() {
    const de = doc.compatMode === 'BackCompat' ? doc.body : doc.documentElement;
    const width = de.clientWidth || win.innerWidth, height = de.clientHeight || win.innerHeight;
    const vv = win.visualViewport;
    const l = vv ? Math.max(0, vv.offsetLeft) : 0, t = vv ? Math.max(0, vv.offsetTop) : 0;
    const r = vv ? Math.min(width, vv.offsetLeft + vv.width) : width, b = vv ? Math.min(height, vv.offsetTop + vv.height) : height;
    // The on-screen keyboard shrinks the visual viewport without zooming: it covers the safe area.
    const keyboard = !!vv && vv.scale < 1.05 && vv.height < height - 120;
    return { width, height, dpr: win.devicePixelRatio || 1, l, t, r, b, keyboard };
  }
  // The safe areas (a phone's home indicator, a notch in landscape) can only be read through an
  // element's style: env() in a CSSOM property (pages with a strict CSP forbid style attributes). The
  // probe sits outside <body> (no page selector like :last-child sees it) and is never page content.
  function readSafe() {
    if (safe) return safe;
    if (!probe) {
      probe = doc.createElement('companions-probe');
      probe.setAttribute('aria-hidden', 'true');
      for (const [k, v] of [['position', 'fixed'], ['left', '0'], ['bottom', '0'], ['width', '0'], ['height', '0'],
        ['visibility', 'hidden'], ['pointer-events', 'none'], ['padding-left', 'env(safe-area-inset-left, 0px)'],
        ['padding-right', 'env(safe-area-inset-right, 0px)'], ['padding-bottom', 'env(safe-area-inset-bottom, 0px)']]) probe.style.setProperty(k, v);
      excluded.add(probe);
      doc.documentElement.append(probe);
    }
    const cs = win.getComputedStyle(probe), px = value => parseFloat(value) || 0;
    safe = { left: px(cs.paddingLeft), right: px(cs.paddingRight), bottom: px(cs.paddingBottom) };
    return safe;
  }

  // ---------- one frame's geometry ----------
  function compose() {
    const view = readView(), S = env.S, t = now();
    covered.clear();
    const rects = new Map(), clipRects = new Map();
    const rectOf = el => { let r = rects.get(el); if (!r) { r = el.getBoundingClientRect(); rects.set(el, r); } return r; };
    const screen = { l: view.l, t: view.t, r: view.r, b: view.b };
    // The visible part of what scroll boxes and clipping ancestors show (their inside, without scrollbars).
    function clipOf(item) {
      let c = clipRects.get(item.clips);
      if (c) return c;
      c = screen;
      for (const el of item.clips) {
        if (!el.isConnected) { c = null; break; }
        const r = rectOf(el), l = r.left + el.clientLeft, top = r.top + el.clientTop;
        c = meet(c, { l, t: top, r: l + (el.clientWidth || r.width), b: top + (el.clientHeight || r.height) });
        if (!c) break;
      }
      clipRects.set(item.clips, c);
      return c;
    }

    const cuts = [], zOf = new Map();
    // Owners: boxes, images, controls and search fields cover what is below them in z.
    for (const o of scan.owners) {
      o.vis = null;
      if (o.type === 'group' || (!o.near && !o.fixed) || !o.el.isConnected) continue;
      const r = rectOf(o.el);
      if (r.width < 1 || r.height < 1) continue;
      const clip = clipOf(o), vis = clip && meet(clip, box(r));
      if (!vis) continue;
      o.vis = vis; o.now = r;
      if (!o.background) cuts.push({ id: o.id, z: o.z, ...vis, item: o.index, ctx: o.ctx });
    }
    // Text: each block's lines, measured once it is on screen, and again only when it changed size.
    for (const b of scan.blocks) {
      b.vis = null;
      if (!b.near || !b.el.isConnected) continue;
      const r = rectOf(b.el), clip = clipOf(b);
      if ((r.width < 1 && r.height < 1) || !clip || !meet(box(r), clip)) continue;
      linesOf(b, r);
      if (!b.lines.length) continue;
      const lines = b.lines.map(q => ({ l: q.l + r.left, t: q.t + r.top, r: q.r + r.left, b: q.b + r.top }));
      if (!lines.some(q => meet(q, clip))) continue;
      b.vis = { r, lines, clip };
      if (b.nav) continue;
      const head = lines.slice(0, -1), last = lines[lines.length - 1];
      for (const [k, q] of [['a', head.length && head.reduce(span)], ['b', last]]) {
        const v = q && meet(q, clip);
        if (v) cuts.push({ id: `${b.id}~${k}`, z: b.owner.z, ...v, item: b.index, ctx: b.ctx });
      }
    }

    const floor = floorOf();
    const surfaces = [];
    const hitsDue = t - lastHits > HIT_EVERY && t - lastScroll > 150;
    let hitBudget = hitsDue ? HIT_BATCH : 0;
    if (hitsDue) lastHits = t;
    // Whether what owns this spot is really on top at a point of it (a dropdown, a dialog or a banner
    // that CSS order alone doesn't reveal). Checked a few at a time, again after scrolling.
    function onTop(item, x, y) {
      if (item.checked === scrollEpoch && t - item.checkedAt < (item.covered ? 800 : 3000)) return !item.covered;
      if (hitBudget <= 0 || x < screen.l || x >= screen.r || y < screen.t || y >= screen.b) return !item.covered;
      hitBudget--;
      item.checked = scrollEpoch; item.checkedAt = t;
      item.covered = coveredAt(item, x, y);
      return !item.covered;
    }
    // From the top down: the item itself, what holds it or what it lies on (anything in the same card,
    // a card made clickable by a stretched link; the photo under a hero title) means it shows. What
    // paints nothing there is looked through: a link stretched over a whole tile, whose text lets the
    // pointer through to it, would else hide the title. Anything else covers it.
    function coveredAt(item, x, y) {
      const self = hostOf(item.scope?.el ?? item.el);
      for (const el of doc.elementsFromPoint(x, y)) {
        if (inExcluded(el)) continue;
        if (el === self || self.contains(el) || el.contains(self) || liesOn(item, el)) return false;
        if (!seeThrough(el, win)) return true;
      }
      return false;
    }
    const liesOn = (item, el) => (byElement.get(el) || []).some(o => item.anc.has(o.index));
    function add(item, owner, surface, probeX, probeY) {
      if (!onTop(item, probeX, probeY)) {
        if (riders) covered.set(surface.id, { item, owner, surface: { ...surface, ownerId: owner.id }, probe: [probeX, probeY] });   // see below
        return;
      }
      surfaces.push({ ...surface, ownerId: owner.id });
      if (riders) tracked.set(surface.id, { item, surface: surfaces[surfaces.length - 1], rect: rectOf(item.el), view, epoch: scrollEpoch,
        probe: [probeX, probeY], coveredUntil: tracked.get(surface.id)?.coveredUntil });
      zOf.set(owner.id, owner.z);
    }

    // The part of [x1, x2] where a small body standing at y covers nothing, and how much room there is
    // above it (`room`, so the engine only lets companions that fit stand there). Things that move with
    // the surface (the same scroll box) trim either end; things that move on their own (fixed bars,
    // other scroll boxes, the screen's edges) only trim the far end, so what a companion stands on never
    // slides under it: when they reach its start, the surface is left out instead. Against those, and
    // the top of the screen, even a tall companion must fit, so riding upwards it drops off in time.
    function free(item, x1, x2, y, s, legs, skip, ownLines = []) {
      const clip = clipOf(item), low = y - MIN_BODY * s, tall = y - TALL_BODY * s, bottom = y + legs;
      if (!clip || tall < clip.t || y > clip.b || y > floor.y) return null;
      // Inside a card, a button or an image, a companion stays inside it: its head doesn't poke out of the top.
      const frame = item.frame?.vis ? item.frame.now.top : -Infinity;
      if (low < frame) return null;
      let pieces = [[x1, x2]];
      const moving = [], above = [];
      const across = q => q.r > x1 && q.l < x2;
      const inBand = (q, top) => q.b > top && q.t < bottom && across(q);
      const sort = (q, still) => {
        if (still ? inBand(q, low) : inBand(q, tall)) { if (still) pieces = without(pieces, q.l, q.r); else moving.push(q); }
        else if (q.b <= y + 1 && q.b > y - ROOM_CAP * s && across(q)) above.push(q);
      };
      for (const q of ownLines) sort(q, true);
      for (const c of cuts) if (!skip(c)) sort(c, c.ctx === item.ctx);
      let best = null;
      for (const p of pieces) if (!best || p[1] - p[0] > best[1] - best[0]) best = p;
      if (!best) return null;
      let [a, b] = best;
      if (a < clip.l) return null;
      b = Math.min(b, clip.r);
      for (const c of moving) {
        if (c.r <= a || c.l >= b) continue;
        if (c.l <= a) return null;
        b = c.l;
      }
      if (b - a < 18 * s) return null;
      let room = Math.min(y - clip.t, y - frame, ROOM_CAP * s);
      for (const q of above) if (q.r > a && q.l < b) room = Math.min(room, y - q.b);
      return { a, b, room: Math.floor(room) };
    }
    const related = (item, c) => c.item === item.index || item.anc.has(c.item) || scan.items[c.item].anc.has(item.index);
    // How far below its top a box's own content starts (its top border and padding); read once per discovery.
    const innerTop = o => (o.inner ??= (() => { const cs = win.getComputedStyle(o.el); return (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.paddingTop) || 0); })());

    // Tops of boxes, images, buttons and search fields, and the insides of round search bars.
    for (const o of scan.owners) {
      if (!o.vis || !o.edge) continue;
      const r = o.now;
      if (o.vis.t > r.top + 0.5) continue;                       // its top is clipped away
      const inset = Math.max(3, o.radius || 0), s = S;
      const span = free(o, r.left + inset, r.right - inset, r.top, s, 0, c => related(o, c));
      // Legs hanging over the edge would cover what starts just inside it (a button's label or icon
      // under a thin padding, a field): there they only stand.
      const tight = span && (o.type === 'search' || (o.type !== 'media' && innerTop(o) < LEGS * s)
        || cuts.some(c => c.item !== o.index && related(o, c) && c.t < r.top + LEGS * s && c.b > r.top && c.l < span.b && c.r > span.a));
      if (span) add(o, o, { id: `${o.id}:top`, kind: 'edge', weight: o.edge.weight, x1: span.a, x2: span.b, y: r.top, room: span.room, ...(tight && { sit: false }) },
        (span.a + span.b) / 2, r.top + 2);
      if (o.type === 'search') pill(o, r);
    }
    function pill(o, r) {
      const h = r.height, s = Math.floor((h - 6) / 8);
      if (s < 2 || r.width < 120 || (o.radius || 0) < 0.3 * h) return;
      if (doc.activeElement === o.el || String(o.el.value ?? o.el.textContent).trim()) return;   // in use: out of the way
      const v = o.vis;
      if (v.l > r.left + 0.5 || v.r < r.right - 0.5 || v.b < r.bottom - 0.5 || r.bottom - 3 > floor.y) return;
      if (cuts.some(c => !related(o, c) && c.z > o.z && c.l < r.right && c.r > r.left && c.t < r.bottom && c.b > r.top)) return;
      const side = o.dir === 'rtl' ? 'L' : 'R';                  // the end away from where the text starts
      add(o, o, { id: `${o.id}:top:in${side}`, kind: 'pill', weight: 1.3, x1: r.left + 4, x2: r.right - 4, y: r.bottom - 3,
        scale: s, pill: { l: r.left, t: r.top, r: r.right, b: r.bottom }, wallSide: side }, side === 'R' ? r.right - h / 2 : r.left + h / 2, r.top + h / 2);
    }

    // Text: on top of a heading's first line, and at the end of lines with room for a body.
    for (const b of scan.blocks) {
      if (!b.vis) continue;
      const { r, lines } = b.vis, s = Math.min(S, b.scale), rtl = b.dir === 'rtl';
      if (b.heading) {
        // Just above the highest ink of its first line (accents, ascenders, an icon), so not even the
        // lowest pixels of a companion standing there (FEET, and the rounding of where it is drawn and
        // of the glyphs' measure) touch a letter. Nobody sits there (sit: false): legs hanging over the
        // edge would cover the line.
        const q = lines[0], top = b.ink == null ? q.t : r.top + b.ink;
        const y = Math.floor(top) - Math.ceil(FEET * Math.min(b.scale, S + 2)) - 1;
        const span = free(b, q.l, q.r, y, s, 0, c => c.item === b.index || b.anc.has(c.item) || within(c, b));
        if (span) add(b, b.owner, { id: `${b.id}:h`, kind: 'edge', weight: b.nav ? 1.4 : 2.2, x1: span.a, x2: span.b, y, scale: b.scale, room: span.room, sit: false, inside: true },
          Math.min(q.r - 2, (span.a + span.b) / 2), (q.t + q.b) / 2);
      }
      if (!b.ends) continue;
      lines.forEach((q, k) => {
        // A seat right after the last word, not the line's whole empty rest: the companion sits at
        // that end (at), so it never floats far from the text. As long as the widest one needs, so
        // the room above is measured just there.
        const y = q.b - (q.b - q.t) * 0.24, end = rtl ? q.l - 3 : q.r + 3;
        const x1 = rtl ? Math.max(r.left + 2, end - SEAT * s) : end, x2 = rtl ? end : Math.min(r.right - 2, end + SEAT * s);
        if (x2 - x1 < 18 * s) return;
        const others = lines.filter((_, i) => i !== k);
        const span = free(b, x1, x2, y, s, LEGS * s, c => c.item === b.index || b.anc.has(c.item), others);
        if (!span || (rtl ? span.b < end - 0.5 : span.a > end + 0.5)) return;   // something right after the word (a badge): no seat
        // (inside: a wide companion keeps its whole body off the last word, not just its middle)
        add(b, b.owner, { id: `${b.id}:t${k}`, kind: 'text', weight: b.heading ? 1.4 : 1, x1: span.a, x2: span.b, y, scale: b.scale, room: span.room,
          inside: true, at: rtl ? 'x2' : 'x1' }, rtl ? q.l + 2 : q.r - 2, (q.t + q.b) / 2);
      });
    }
    function within(c, b) {                                      // its own links and buttons don't block a heading's or nav's top
      const el = scan.items[c.item].el;
      return el !== b.el && b.el.contains(el);
    }

    if (surfaces.length > maxSurfaces) surfaces.sort((p, q) => q.weight - p.weight).length = maxSurfaces;
    if (riders) {
      const occupied = new Set(riders()), present = new Set(surfaces.map(s => s.id));
      // An occupied spot with something on top of it (a dialog and its backdrop, a dialog's panel, a
      // banner) stays where it is, `covered`: the companion waits on it out of sight and is back there
      // once it shows again. Either the spot is all there but the hit test finds something on top of it,
      // or it is left out (that something cuts it) while its element is just where and as it was.
      for (const [id, { item, owner, surface, probe }] of covered) {
        if (!occupied.has(id) || present.has(id)) continue;
        surfaces.push({ ...surface, riding: true, covered: true }); present.add(id); zOf.set(owner.id, owner.z);
        tracked.set(id, { item, surface, rect: rectOf(item.el), view, epoch: scrollEpoch, probe, coveredUntil: t + STAY_COVERED });
      }
      // What is on top of a spot can flicker while it fades in or out (a dialog's opacity animation makes it
      // see-through for a moment): a spot found covered stays so for a little while.
      for (const s of surfaces) {
        if (!s.riding && occupied.has(s.id) && tracked.get(s.id)?.coveredUntil > t) Object.assign(s, { riding: true, covered: true });
      }
      // (A hit test now and then, not every frame.)
      const onTopOfIt = saved => {
        if (!(t - saved.hitAt < 250)) { saved.hitAt = t; saved.hit = coveredAt(saved.item, saved.probe[0], saved.probe[1]); }
        return saved.hit;
      };
      for (const [id, saved] of tracked) {
        if (!occupied.has(id)) { tracked.delete(id); continue; }
        if (present.has(id)) continue;
        const { item, surface, rect, view: before } = saved, el = item.el;
        if (!el.isConnected || before.width !== view.width || before.height !== view.height) continue;
        const r = rectOf(el), cs = win.getComputedStyle(el);
        if (cs.display === 'none' || cs.visibility !== 'visible' || parseFloat(cs.opacity) < 0.05
          || el.closest('[hidden], [aria-hidden="true"], [data-companions-ignore]')) continue;
        if (Math.abs(r.width - rect.width) > 0.5 || Math.abs(r.height - rect.height) > 0.5) continue;
        const dx = r.left - rect.left, dy = r.top - rect.top, y = surface.y + dy;
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
          if (onTopOfIt(saved)) saved.coveredUntil = t + STAY_COVERED;
          if (saved.coveredUntil > t) surfaces.push({ ...surface, riding: true, covered: true });
          continue;
        }
        if (saved.epoch === scrollEpoch || Math.abs(dy) < 0.5) continue;
        // Only the window's vertical clipping is forgiven. Back in view, the ordinary scan must
        // offer the spot again: changed text, a cover or lost room then gets normal recovery.
        if (y >= view.t + TALL_BODY * S && y <= floor.y) continue;
        if (item.clips.some(el => { const q = rectOf(el); return y < q.top || y > q.bottom; })) continue;
        const pill = surface.pill;
        surfaces.push({ ...surface, x1: surface.x1 + dx, x2: surface.x2 + dx, y, riding: true,
          ...(pill && { pill: { l: pill.l + dx, r: pill.r + dx, t: pill.t + dy, b: pill.b + dy } }) });
      }
    }
    const occluders = [];
    for (const c of cuts) { occluders.push({ id: c.id, z: c.z, l: c.l, t: c.t, r: c.r, b: c.b }); zOf.delete(c.id); }
    // Owners without a box of their own (a group of text, a page-sized background) still need their z.
    for (const [id, z] of zOf) occluders.push({ id, z, l: 0, t: 0, r: 0, b: 0 });
    return { viewport: { width: view.width, height: view.height, dpr: view.dpr }, surfaces, occluders, floor };

    // The bottom of what is visible, above the phone's safe area and the keyboard, and inside the
    // side safe areas; a fixed bar along the bottom is the floor itself. The floor lies over the page,
    // so companions would rather stand on the page's own spots (its weight), and stay on it only
    // where no content passes under it (rest).
    function floorOf() {
      const inset = readSafe();
      let y = view.b - (view.keyboard ? 0 : inset.bottom) - 6;
      const width = view.r - view.l;
      for (const o of scan.owners) {
        const v = o.vis;
        if (!v || !o.fixed || o.type === 'group' || v.b < view.b - 2 || v.t < view.b - 0.45 * view.height) continue;
        if (v.r - v.l >= 0.7 * width) y = Math.min(y, v.t);
      }
      const floor = { x1: view.l + inset.left, x2: view.r - inset.right, y, weight: FLOOR_WEIGHT };
      floor.rest = clearOf(floor, S);
      return floor;
    }
  }

  // A block's lines (measure()), from the cache while its content, fonts and size stay the same.
  function linesOf(b, r) {
    const cached = !b.lines && !b.nav && lineCache.get(b.el);
    if (cached && cached.content === content) { b.lines = cached.lines; b.measured = cached.measured; b.ink = cached.ink; }
    if (!b.lines || Math.abs(b.measured.w - r.width) > 0.5 || Math.abs(b.measured.h - r.height) > 0.5) measure(b, r);
  }

  // The stretches of the floor that no page content passes over while the page scrolls (its side
  // margins), as [x1, x2] pairs in client pixels. Text (by its lines), controls, search fields and
  // media count across the whole discovered band (two screens above and below); fixed and sticky ones
  // only where they come down to the floor. Boxes count by what is in them, not their background.
  // What passes under the floor only changes sideways, so this holds until the next discovery (or a
  // resize, or another floor or scale).
  let clear = null;
  function clearOf(floor, scale) {
    const key = `${scans}|${content}|${scale}|${floor.x1}|${floor.x2}|${floor.y}`;
    if (clear?.key === key) return clear.pieces;
    const top = floor.y - TALL_BODY * scale;
    let pieces = [[floor.x1, floor.x2]];
    const cut = q => { if (q.r > q.l && (!q.fixed || (q.b > top && q.t < floor.y))) pieces = without(pieces, q.l, q.r); };
    for (const item of scan.items) {
      const text = !!item.runs;
      if ((!text && !CONTENT.has(item.type)) || !item.el.isConnected) continue;
      const fixed = item.layer > 0, r = fixed || text ? item.el.getBoundingClientRect() : item.rect;
      if (r.width < 1 && r.height < 1) continue;
      if (!text) { cut({ l: r.left, t: r.top, r: r.right, b: r.bottom, fixed }); continue; }
      linesOf(item, r);
      for (const q of item.lines) cut({ l: q.l + r.left, t: q.t + r.top, r: q.r + r.left, b: q.b + r.top, fixed });
    }
    clear = { key, pieces };
    return pieces;
  }

  // The lines of a block: its own inline content's rectangles, merged per line, relative to the block.
  function measure(b, r) {
    const range = doc.createRange(), rects = [];
    for (const [first, last] of b.runs) {
      if (!first) range.selectNodeContents(b.el);
      else if (!first.isConnected || !last.isConnected) continue;
      else { range.setStartBefore(first); range.setEndAfter(last); }
      for (const q of range.getClientRects()) if (q.width >= 1 && q.height >= 2) rects.push(q);
    }
    const lines = [];
    for (const q of rects) {
      const mid = (q.top + q.bottom) / 2;
      const line = lines.find(L => (mid >= L.t && mid <= L.b) || ((L.t + L.b) / 2 >= q.top && (L.t + L.b) / 2 <= q.bottom));
      if (line) { line.l = Math.min(line.l, q.left); line.r = Math.max(line.r, q.right); line.t = Math.min(line.t, q.top); line.b = Math.max(line.b, q.bottom); }
      else lines.push({ l: q.left, t: q.top, r: q.right, b: q.bottom });
    }
    lines.sort((p, q) => p.t - q.t);
    b.lines = lines.map(L => ({ l: L.l - r.left, t: L.t - r.top, r: L.r - r.left, b: L.b - r.top }));
    b.measured = { w: r.width, h: r.height };
    // Where the ink of a heading's first line starts, for the spot on top of it (ink.js).
    const top = b.heading && lines.length ? ink.lineTop(b.el, b.runs, lines[0]) : null;
    b.ink = top == null ? null : top - r.top;
    if (!b.nav) lineCache.set(b.el, { lines: b.lines, measured: b.measured, ink: b.ink, content });
  }

  function snapshot() {
    if (destroyed) return EMPTY;
    const t = now();
    if (cache && !stale && t - cacheAt < SAME_FRAME) return cache;
    if (!scan || (dirty && !subscribers.size)) rescan();
    if (riders) {
      // A retained element can return from beyond the discovery band in a single scroll. Discover
      // it now, before deciding its old spot is gone; don't wait for the throttled rediscovery.
      for (const saved of tracked.values()) {
        const r = saved.item.el.getBoundingClientRect(), y = saved.surface.y + r.top - saved.rect.top;
        if (y < 0 || y > readView().height) continue;
        if (!scan.items.some(item => item.el === saved.item.el)) { rescan(); break; }
      }
      const occupied = new Set(riders().map(id => id.split(':')[0]));
      for (const item of scan.items) if (occupied.has(item.id)) item.near = true;
    }
    cache = compose(); cacheAt = t; stale = false;
    return cache;
  }

  return {
    snapshot,
    subscribe(changed) {
      if (destroyed || typeof changed !== 'function') return () => {};
      subscribers.add(changed);
      schedule();
      return () => { subscribers.delete(changed); };
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      win.clearTimeout(timer); timer = null;
      mutations.disconnect(); resizes.disconnect(); nearby.disconnect();
      events.abort();
      if (probe) { probe.remove(); excluded.delete(probe); probe = null; }
      subscribers.clear(); watchedRoots.clear(); byElement.clear();
      tracked.clear(); covered.clear();
      scan = null; cache = null;
    },
    // The stretches of the floor that no page content passes over while the page scrolls (its side
    // margins), as [x1, x2] pairs in client pixels: where companions may stay on the floor without
    // covering anything (the snapshot's floor.rest, for this scale).
    clearFloor(scale = env.S) {
      if (destroyed) return [];
      return clearOf(snapshot().floor, scale).map(p => [...p]);
    },
    // The id this world gives an element: its owner id, and the prefix of its surfaces' ids
    // (`${idOf(el)}:top`), for a host that wants to start a companion somewhere.
    idOf: el => (el && typeof el === 'object' ? idOf(el) : null),
    // The element's own opening spots, best first, by the same heading rule as discovery (including
    // large text and navigation): above a heading's letters, else the top of its box (a heading styled
    // as a box, with a background, may only have that), and the other way round for anything else.
    // Room and visibility are checked later, at the runtime's actual size.
    startIds(el) {
      if (!scan) rescan();
      const id = idOf(el);
      return scan.blocks.some(b => b.el === el && b.heading) ? [id + ':h', id + ':top'] : [id + ':top', id + ':h'];
    },
    // Discovery numbers for checks and debugging.
    stats: () => (scan ? { scans, owners: scan.owners.length, blocks: scan.blocks.length, visited: scan.visited, truncated: scan.truncated, ms: scan.ms } : null),
  };
}

// ---------- small geometry ----------
const box = r => ({ l: r.left, t: r.top, r: r.right, b: r.bottom });
const span = (p, q) => ({ l: Math.min(p.l, q.l), t: Math.min(p.t, q.t), r: Math.max(p.r, q.r), b: Math.max(p.b, q.b) });
function meet(p, q) {
  if (!p || !q) return null;
  const l = Math.max(p.l, q.l), t = Math.max(p.t, q.t), r = Math.min(p.r, q.r), b = Math.min(p.b, q.b);
  return r > l && b > t ? { l, t, r, b } : null;
}
// The intervals left when [l, r] is taken out of each.
function without(pieces, l, r) {
  const out = [];
  for (const [a, b] of pieces) {
    if (r <= a || l >= b) { out.push([a, b]); continue; }
    if (l > a) out.push([a, l]);
    if (r < b) out.push([r, b]);
  }
  return out;
}
// Hit tests from the document see the outermost shadow host of an element inside a shadow tree.
function hostOf(el) {
  let n = el;
  for (let root = n.getRootNode(); root && root.host; root = n.getRootNode()) n = root.host;
  return n;
}
